/**
 * TicketPreviewModal.tsx
 *
 * Vista previa obligatoria del ticket antes/después de imprimir.
 * Renderiza el ticket con el mismo payload que se usaría en impresión.
 * Respeta ancho real (80mm / 58mm).
 * Incluye opción de enviar por WhatsApp como imagen JPG.
 *
 * Props:
 *  - payload: TicketPayload (null = cerrado)
 *  - onClose: cerrar modal
 */
import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { Modal, Button } from '../../../components/ui';
import { Printer, X, Send, Bluetooth } from 'lucide-react';
import { TicketPayload, renderTicketPreviewHTML, imprimirTicket } from '../../../services/ticketService';
import { payloadToTicketText } from '../../../utils/printer';
import { connectPrinter, printTicketText, detectRecommendedMethod } from '../../../utils/printer';
import { printTicketByDestination, isNativePrintSelected } from '../../../utils/printer';
import { shareTicketAsImage, generateTicketImage } from '../../../utils/ticketImageShare';
import { isNativePlatform } from '../../../utils/platform';
import { openWhatsApp, buildTicketWhatsappText } from '../../../utils/whatsappShare';
import { shareImageNative } from '../../../utils/nativeShare';

interface TicketPreviewModalProps {
  payload: TicketPayload | null;
  onClose: () => void;
  autoShow?: boolean;
  readOnly?: boolean;
}

export const TicketPreviewModal: React.FC<TicketPreviewModalProps> = ({
  payload,
  onClose,
  autoShow = true,
  readOnly = false,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [printed, setPrinted] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [printError, setPrintError] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);
  const [shareStatus, setShareStatus] = useState<string | null>(null);
  const [apkImageUrl, setApkImageUrl] = useState<string | null>(null);
  const [apkImageBlob, setApkImageBlob] = useState<Blob | null>(null);
  const [bluetoothConectando, setBluetoothConectando] = useState(false);
  const [bluetoothImprimiendo, setBluetoothImprimiendo] = useState(false);
  const [bluetoothEstado, setBluetoothEstado] = useState<string | null>(null);

  const isOpen = !!payload && autoShow;
  const isNativePrint = useMemo(() => isNativePrintSelected(), []);

  // El HTML se arma UNA vez por payload. Con tickets de 100–200 jugadas cada
  // render completo cuesta, y antes se rehacía en cada re-render del modal
  // (printing, sharing, estado de bluetooth… todos disparan render).
  const ticketHTML = useMemo(
    () => (payload ? renderTicketPreviewHTML(payload) : null),
    [payload],
  );

  // Pre-calcular texto ESC/POS una sola vez por payload (evita recálculo en cada reimpresión)
  const ticketTextRef = useRef<string | null>(null);
  const ticketTextForPayload = useCallback((p: TicketPayload): string => {
    if (ticketTextRef.current && printed) {
      // Si ya se calculó para este payload y es reimpresión, reusar
      return ticketTextRef.current;
    }
    const text = payloadToTicketText({
      ticketCode: p.ticketCode,
      sorteoNombre: p.sorteoNombre,
      fecha: p.fecha,
      total: p.total,
      jugadas: p.jugadas,
    });
    ticketTextRef.current = text;
    return text;
  }, [printed]);

  // Escribir HTML del ticket en el iframe cuando cambie el payload
  // En APK nativa con auto-print, se difiere el render del iframe para no bloquear
  useEffect(() => {
    if (!payload || !iframeRef.current || !ticketHTML) return;

    if (isNativePrint) {
      // APK: defer iframe render — auto-print will fire first
      const timer = setTimeout(() => {
        if (iframeRef.current) {
          iframeRef.current.srcdoc = ticketHTML;
        }
      }, 500);
      // Reset state immediately
      setPrinted(false);
      setPrintError(null);
      setShareStatus(null);
      setApkImageUrl(null);
      setApkImageBlob(null);
      ticketTextRef.current = null;
      return () => clearTimeout(timer);
    }

    iframeRef.current.srcdoc = ticketHTML;

    // Reset state
    setPrinted(false);
    setPrintError(null);
    setShareStatus(null);
    setApkImageUrl(null);
    setApkImageBlob(null);
    ticketTextRef.current = null;
    setBluetoothEstado(
      detectRecommendedMethod() !== 'browser-print' ? 'Impresora disponible' : null,
    );
  }, [payload, isNativePrint, ticketHTML]);

  const handlePrint = useCallback(async () => {
    if (!payload || printing) return;

    console.log('[APK TICKET] ─────────────────────────────────');
    console.log('[APK TICKET] step 1 click boton imprimir ticket');
    console.log('[APK TICKET] ticket:', payload.ticketCode);
    console.log('[APK TICKET] isNativePrint:', isNativePrint);

    setPrinting(true);
    setPrintError(null);

    // Safety timeout: GARANTIZA que printing se limpia en max 10 segundos
    const safetyTimer = setTimeout(() => {
      console.error('[APK TICKET] SAFETY TIMEOUT 10s - forzando cleanup');
      setPrinting(false);
      setPrintError('Timeout: la impresión tardó demasiado. Intenta de nuevo.');
    }, 10000);

    const method = detectRecommendedMethod();
    console.log('[APK TICKET] step 2 method:', method);

    try {
      if (isNativePrint) {
        console.log('[APK TICKET] step 3 entrando ruta nativa (printTicketByDestination)');
        const result = await printTicketByDestination({
          ticketCode: payload.ticketCode,
          sorteoNombre: payload.sorteoNombre,
          fecha: payload.fecha,
          total: payload.total,
          jugadas: payload.jugadas,
          localNombre: payload.localNombre,
          localDescripcion: payload.local?.descripcion || undefined,
          sorteoCierre: payload.sorteoCierre,
          vendedorNombre: payload.vendedorNombre,
          clienteNombre: payload.clienteNombre,
          pagaVecesAplicado: payload.pagaVecesAplicado,
          esReimpresion: payload.esReimpresion,
          leyendaLocal: payload.config?.leyendaLocal || undefined,
          localTelefono: payload.local?.telefono || undefined,
          fechaOperativa: payload.fechaOperativa || undefined,
        });

        if (result.ok) {
          console.log('[APK TICKET] step 5 success');
          setPrinted(true);
          onClose();
        } else {
          console.error('[APK TICKET] step 5 falló:', result.error);
          setPrintError(result.error || 'Error al imprimir');
        }
        return;
      }

      // Web: window.print()
      console.log('[APK TICKET] ruta WEB (no es SUNMI)');
      const ok = imprimirTicket(payload);
      if (ok) {
        setPrinted(true);
        console.log('[APK TICKET] step 5 success web');
        onClose();
      } else {
        setPrintError('No se pudo abrir la ventana de impresión.');
      }
    } catch (err: any) {
      const errMsg = err?.message || String(err) || 'Error desconocido';
      console.error('[APK TICKET] step 5 error:', errMsg);
      setPrintError(errMsg);
    } finally {
      clearTimeout(safetyTimer);
      setPrinting(false);
      console.log('[APK TICKET] step 6 finally - printing cleared');
      console.log('[APK TICKET] ─────────────────────────────────');
    }
  }, [payload, printing, onClose, ticketTextForPayload, isNativePrint]);

  const handleWhatsApp = useCallback(async () => {
    if (!payload) return;

    setSharing(true);
    setShareStatus('Generando imagen...');
    setPrintError(null);

    try {
      if (isNativePlatform()) {
        // APK: generar imagen y mostrar preview con botones
        const { dataUrl, blob } = await generateTicketImage(payload);
        setApkImageUrl(dataUrl);
        setApkImageBlob(blob);
        setShareStatus(null);
      } else {
        // Web: compartir directo
        setShareStatus('Selecciona WhatsApp para enviar...');
        await shareTicketAsImage(payload);
        setShareStatus('Compartido exitosamente');
      }
    } catch (err: any) {
      console.error('[TicketPreview] Error al compartir:', err);
      if (err.name !== 'AbortError') {
        setPrintError(err.message || 'Error al compartir por WhatsApp');
      }
      setShareStatus(null);
    } finally {
      setSharing(false);
    }
  }, [payload]);

  const handleConectarBluetooth = useCallback(async () => {
    setBluetoothConectando(true);
    setPrintError(null);
    try {
      const result = await connectPrinter();
      setBluetoothEstado(`Conectada: ${result.name} (${result.method})`);
    } catch (err: any) {
      console.error('[TicketPreview] Error conectando:', err);
      setPrintError(err?.message || 'No se pudo conectar la impresora.');
    } finally {
      setBluetoothConectando(false);
    }
  }, []);

  const handlePrintBluetooth = useCallback(async () => {
    if (!payload) return;

    setBluetoothImprimiendo(true);
    setPrintError(null);

    try {
      const text = ticketTextForPayload(payload);
      await printTicketText(text, { ancho: payload.config.ancho });
      setBluetoothEstado('Ticket enviado');
      setPrinted(true);
    } catch (err: any) {
      console.error('[TicketPreview] Error imprimiendo:', err);
      setPrintError(err?.message || 'No se pudo imprimir.');
    } finally {
      setBluetoothImprimiendo(false);
    }
  }, [payload, ticketTextForPayload]);

  // APK SUNMI: auto-imprimir al abrir el modal (sin esperar click del usuario)
  useEffect(() => {
    if (readOnly) return;
    if (!payload || !isNativePrint || printing || printed) {
      console.log('[APK TICKET AUTO] skip:', { payload: !!payload, isNativePrint, printing, printed });
      return;
    }

    console.log('[APK TICKET AUTO] programando auto-print en 50ms...');
    const timer = setTimeout(() => {
      console.log('[APK TICKET AUTO] ejecutando handlePrint()');
      handlePrint();
    }, 50);

    return () => clearTimeout(timer);
  }, [payload, isNativePrint, printing, printed, handlePrint]);

  // Atajo de teclado: Enter = imprimir, ESC = cerrar
  useEffect(() => {
    if (!isOpen || readOnly) return;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === '*') {
        e.preventDefault();
        e.stopPropagation();
        handlePrint();
      }
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, handlePrint]);

  if (!payload) return null;

  const anchoMM = payload.config.ancho;
  const anchoPx = anchoMM === 58 ? 384 : 512;
  const isNative = isNativePlatform();

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="" size={isNative ? 'sm' : 'lg'}>
      <div className={`flex flex-col items-center ${isNative ? 'gap-2' : 'gap-4'}`}>
        {/* Reimpresión banner */}
        {payload.esReimpresion && (
          <div className="w-full bg-red-50 border-2 border-dashed border-red-400 text-red-700 text-center py-2 px-4 rounded-lg font-bold text-sm tracking-wider">
            *** REIMPRESIÓN ***
          </div>
        )}

        {/* Label */}
        <div className="text-center">
          <h3 className={`font-bold text-gray-900 ${isNative ? 'text-base' : 'text-lg'}`}>
            {readOnly ? 'Detalle del ticket' : (payload.esReimpresion ? 'Reimpresión de ticket' : 'Vista previa del ticket')}
          </h3>
          <p className="text-sm text-gray-500">
            Ticket: <span className="font-mono font-bold">{payload.ticketCode}</span>
            {' · '}Ancho: {anchoMM}mm
          </p>
        </div>

        {/* Ticket preview iframe — en APK nativo se reduce para que quepan los botones */}
        <div
          className="border-2 border-gray-300 rounded-lg shadow-inner bg-white overflow-hidden"
          style={{
            width: isNative ? '100%' : `${Math.min(anchoPx + 16, 528)}px`,
            maxWidth: `${Math.min(anchoPx + 16, 528)}px`,
            ...(apkImageUrl ? { display: 'none' } : {}),
          }}
        >
          <iframe
            ref={iframeRef}
            title="Ticket Preview"
            style={{
              width: `${anchoPx}px`,
              minHeight: isNative ? '200px' : '400px',
              maxHeight: isNative ? '35vh' : '600px',
              border: 'none',
              margin: '8px auto',
              display: 'block',
            }}
            sandbox="allow-scripts"
          />
        </div>

        {/* Error */}
        {printError && (
          <div className="w-full bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-lg text-sm">
            {printError}
          </div>
        )}

        {/* Printed confirmation */}
        {printed && (
          <div className="w-full bg-green-50 border border-green-200 text-green-700 px-4 py-2 rounded-lg text-sm text-center">
            ✓ Enviado a impresión
          </div>
        )}

        {/* Share status */}
        {shareStatus && (
          <div className="w-full bg-blue-50 border border-blue-200 text-blue-700 px-4 py-2 rounded-lg text-sm text-center">
            {shareStatus}
          </div>
        )}

        {/* SUNMI status */}
        {isNativePrint && (
          <div className="w-full bg-green-50 border border-green-200 text-green-700 px-4 py-2 rounded-lg text-sm text-center">
            Impresion nativa activa
          </div>
        )}

        {/* Bluetooth status (solo web) */}
        {!isNativePrint && bluetoothEstado && (
          <div className="w-full bg-indigo-50 border border-indigo-200 text-indigo-700 px-4 py-2 rounded-lg text-sm text-center">
            {bluetoothEstado}
          </div>
        )}

        {/* APK Image Preview — se muestra después de generar imagen */}
        {apkImageUrl && (
          <div className="flex flex-col items-center gap-3 w-full max-w-sm">
            <div className="border border-gray-300 rounded-lg overflow-hidden bg-white shadow-inner">
              <img
                src={apkImageUrl}
                alt={`Ticket ${payload.ticketCode}`}
                className="max-w-full max-h-[50vh] object-contain"
              />
            </div>
            <div className="flex gap-2 w-full">
              {/* Cerrar: vuelve al modal normal */}
              <Button
                variant="secondary"
                onClick={() => {
                  setApkImageUrl(null);
                  setApkImageBlob(null);
                }}
                className="flex-1"
              >
                <X size={16} className="mr-1 inline" />
                Cerrar
              </Button>

              {/*
                Compartir: usa Capacitor Share para enviar la IMAGEN vía share sheet nativo.
                En Android abre selector de apps (WhatsApp, Telegram, etc.)
                Fallback: descarga el JPG.
              */}
              <Button
                variant="primary"
                onClick={async () => {
                  if (!apkImageBlob) return;
                  const fileName = `ticket-${payload.ticketCode}.jpg`;
                  try {
                    await shareImageNative(
                      apkImageBlob,
                      fileName,
                      `Ticket ${payload.ticketCode}`,
                      `🎟 Ticket: ${payload.ticketCode} · Total: ₡${Math.trunc(payload.total).toLocaleString('es-CR')}`,
                    );
                  } catch (err: any) {
                    if (err?.name !== 'AbortError') {
                      console.error('[TicketPreview] Error compartiendo imagen:', err);
                    }
                  }
                }}
                className="flex-1 bg-green-600 hover:bg-green-500 flex items-center justify-center gap-1"
              >
                <Send size={16} /> Compartir
              </Button>
            </div>
            <p className="text-xs text-gray-400 text-center">
              Compartir envía la imagen del ticket
            </p>
          </div>
        )}

        {/* Actions */}
        {!apkImageUrl && readOnly && (
          <div className="w-full max-w-2xl flex justify-center">
            <Button
              variant="secondary"
              onClick={onClose}
              className="flex items-center justify-center gap-2 px-8"
            >
              <X size={16} />
              Cerrar
            </Button>
          </div>
        )}
        {!apkImageUrl && !readOnly && (
          <>
            {isNative ? (
              /* APK: botones en stack vertical, imprimir primero, tamaño grande */
              <div className="flex flex-col gap-2 w-full">
                <Button
                  variant="primary"
                  onClick={handlePrint}
                  disabled={printing}
                  className="w-full bg-green-600 hover:bg-green-500 flex items-center justify-center gap-2 min-h-[48px] text-base"
                >
                  <Printer size={18} />
                  {printing ? 'Imprimiendo...' : (printed ? 'Reimprimir' : 'Imprimir')}
                </Button>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    onClick={handleWhatsApp}
                    disabled={sharing}
                    className="flex-1 flex items-center justify-center gap-1 bg-green-100 hover:bg-green-200 text-green-800 border-green-300 min-h-[44px]"
                  >
                    <Send size={16} />
                    {sharing ? 'Generando...' : 'WhatsApp'}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={onClose}
                    className="flex-1 flex items-center justify-center gap-1 min-h-[44px]"
                  >
                    <X size={16} />
                    Cerrar
                  </Button>
                </div>
              </div>
            ) : (
              /* Web: grid original */
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 w-full max-w-2xl">
                  <Button
                    variant="secondary"
                    onClick={onClose}
                    className="flex items-center justify-center gap-2"
                  >
                    <X size={16} />
                    Cerrar
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleWhatsApp}
                    disabled={sharing}
                    className="flex items-center justify-center gap-2 bg-green-100 hover:bg-green-200 text-green-800 border-green-300"
                  >
                    <Send size={16} />
                    {sharing ? 'Generando...' : 'WhatsApp'}
                  </Button>
                  {!isNativePrint && (
                    <Button
                      variant="secondary"
                      onClick={handleConectarBluetooth}
                      disabled={bluetoothConectando}
                      className="flex items-center justify-center gap-2 bg-indigo-100 hover:bg-indigo-200 text-indigo-800 border-indigo-300"
                    >
                      <Bluetooth size={16} />
                      {bluetoothConectando ? 'Conectando...' : 'Bluetooth'}
                    </Button>
                  )}
                  <Button
                    variant="primary"
                    onClick={handlePrint}
                    disabled={printing}
                    className="bg-green-600 hover:bg-green-500 flex items-center justify-center gap-2"
                  >
                    <Printer size={16} />
                    {printing ? 'Imprimiendo...' : (printed ? 'Reimprimir' : 'Imprimir')}
                  </Button>
                </div>

                {!isNativePrint && (
                  <div className="w-full max-w-2xl">
                    <Button
                      variant="secondary"
                      onClick={handlePrintBluetooth}
                      disabled={bluetoothImprimiendo}
                      className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white border-indigo-500"
                    >
                      <Bluetooth size={16} />
                      {bluetoothImprimiendo ? 'Enviando a impresora Bluetooth...' : 'Imprimir por Bluetooth (ESC/POS)'}
                    </Button>
                  </div>
                )}
              </>
            )}

            <p className="text-xs text-gray-400">
              Enter o * para imprimir · ESC para cerrar
            </p>
          </>
        )}
      </div>
    </Modal>
  );
};

export default TicketPreviewModal;
